import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { VoidWall, SolidWall } from "building-types/walls/wallTypes";
import { line2Normal, line2PerpDistance } from "geom/line2";
import { offsetPolyLine2Variable } from "geom/offset";
import { polyLine2HasNaN, polyLine2ReverseCopy, polyLine2EdgeCount, polyLine2EdgeStart } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { getWallPath } from "./wall";

export function computeThresholdsForVoidWall(wall: VoidWall, outlinesOut: PolyLine2Polygon[]) {
  if (wall.path.leftRoom?.attributes.openToBelow && wall.path.rightRoom?.attributes.openToBelow) {
    return;
  }

  if (!wall.path.leftRoom?.attributes.openToBelow && !wall.path.rightRoom?.attributes.openToBelow) {
    return;
  }

  const { startX, startY, endX, endY } = wall.path;

  const [nx, ny] = line2Normal(startX, startY, endX, endY);

  const { leftOffset, rightOffset } = wall;

  const offset = wall.path.leftRoom?.attributes.openToBelow ? -leftOffset : rightOffset;

  const [soX, soY, eoX, eoY] = [startX + nx * offset, startY + ny * offset, endX + nx * offset, endY + ny * offset];

  const p: PolyLine2 = polyLine2New([startX, startY, endX, endY, eoX, eoY, soX, soY, startX, startY]);

  if (!polyLine2HasNaN(p)) {
    outlinesOut.push([p]);
  }
}

export function computeThresholdsForSolidWall(wall: SolidWall, room: Room, outlinesOut: PolyLine2Polygon[]) {
  const { doorPlacements: doors } = wall;
  const path = getWallPath(wall);
  const { startX, startY, endX, endY } = path;

  for (const { opening } of doors) {
    if (!opening) {
      continue;
    }

    const { frontRoom, backRoom } = opening;
    const planOutline = wallOpeningPlanExtents(opening);
    const isFrontRoom = frontRoom === room;

    const fClass = frontRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;
    const bClass = backRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;

    const frontRoomPriority = fClass < bClass;
    const backRoomPriority = bClass < fClass;

    const isPriority = (isFrontRoom && frontRoomPriority) || (!isFrontRoom && backRoomPriority);
    const isNotPriority = (frontRoomPriority || backRoomPriority) && !isPriority;

    if (isPriority) {
      const faceLoop = polyLine2ReverseCopy(planOutline);
      outlinesOut.push([faceLoop]);
      continue;
    } else if (isNotPriority) {
      continue;
    }

    const outlineEdgeCount = polyLine2EdgeCount(planOutline);
    if (outlineEdgeCount < 4) {
      continue;
    }
    const index = isFrontRoom ? 1 : 3;
    const oppIndex = (index + 2) % outlineEdgeCount;
    const [x, y] = polyLine2EdgeStart(planOutline, oppIndex);
    const offset = line2PerpDistance(startX, startY, endX, endY, x, y);

    const offsets = new Array<number>(outlineEdgeCount).fill(0);
    offsets[index] = offset;

    const faceLoop = offsetPolyLine2Variable(polyLine2ReverseCopy(planOutline), offsets);
    outlinesOut.push([faceLoop]);
  }
}
